// src/pages/IncomeCard.js
//
// One income-type card for Step 2 of the calculator.
// Toggled on -> shows one or more IncomeItem mini-forms (one per "Add another").
// Toggled off -> no items of this income_type are included in the session.
//
// Field visibility is driven by INCOME_CARD_CONFIG (incomeCardConfig.js).
// All amounts entered "as-is" — period/currency conversion happens server-side
// in irpfStandalone.ts via normalizeItemAmounts(). This component only sets
// amount_period, amount_basis, input_precision, currency, and the *_original
// amount fields; it never computes *_eur fields.
//
// RENTAL ITEMS: amount_basis and net_amount_original are owned entirely by
// the RentalSection below — not by the standard gross/net select. The card
// computes net_amount_original = gross - sumExpenses - depreciation and writes
// it back on every change so normalizeItemAmounts() receives the correct net.

import React, { useMemo, useState } from 'react'
import {
  AMOUNT_BASIS_ASK_TYPES,
  AMOUNT_BASIS_OPTIONS,
  PERIOD_OPTIONS,
  CURRENCY_OPTIONS,
  SOURCE_COUNTRY_OPTIONS,
  ACTIVITY_CLASSIFICATION_OPTIONS,
  createEmptyIncomeItem,
} from '../hooks/incomeCardConfig'
import { Field, Select, NumberField } from './CalculatorFields'
import { YES_NO_OPTIONS, boolToOptionValue, optionValueToBool } from '../hooks/autonomoFactsConfig'
import posthog from '../lib/posthog'

// NumberField returns string values from onChange — wrap with numeric adapter.
export function AmountField({ value, onChange, onBlur, placeholder }) {
  return (
    <NumberField
      value={value === 0 ? '' : value}
      onChange={v => onChange(v === '' ? 0 : Number(v))}
      onBlur={onBlur}
      placeholder={placeholder}
    />
  )
}

// ── Period-mismatch nudge ──────────────────────────────────────────────────
// If "Annual" is selected but the entered amount is well below a plausible
// annual figure, the user most likely left the Period dropdown on its
// default instead of switching to "Monthly" — the real report that prompted
// this (a $2,500/$2,000 "annual" pension + dividends session yielding $0
// IRPF) was exactly this. Soft nudge only, never blocks or auto-corrects.
//
// TODO: currently only consumed on this page. Move into incomeCardConfig.js
// (alongside PERIOD_OPTIONS) if/when a second consumer shows up — see
// sharedConstants.js gap noted in calculators_reference.md.
const LOW_ANNUAL_INCOME_THRESHOLD = 10000
const PERIOD_MISMATCH_WARNING = 'Is this a monthly amount? If so, please select "per month" in the dropdown.'

// ── Rental helpers ────────────────────────────────────────────────────────────

const CURRENCY_SYMBOLS = { EUR: '€', USD: '$', GBP: '£' }
function fmtRentalAmount(amount, currency) {
  const sym = CURRENCY_SYMBOLS[currency?.toUpperCase()] ?? (currency + ' ')
  return sym + Math.round(Math.max(0, amount || 0)).toLocaleString('en')
}

function sumRentalExpenses(expenses) {
  if (!expenses) return 0
  return Object.values(expenses).reduce((sum, v) => sum + (Number(v) || 0), 0)
}

// Recomputes net_amount_original and amount_basis from rental fields.
// Called after any rental field changes so the item sent to the engine is correct.
function deriveRentalNet(item) {
  const { rental_expense_path, gross_amount_original, rental_expenses, rental_depreciation_amount } = item
  if (rental_expense_path === 'fast_path_net') {
    // User entered net after cash expenses. Subtract depreciation (same original currency)
    // before writing net_amount_original — FX conversion happens later in the engine.
    const depreciation = Number(item.rental_depreciation_amount) || 0
    const net = Math.max(0, (item._fast_path_net_entered || 0) - depreciation)
    return {
      ...item,
      amount_basis: depreciation > 0 ? 'gross_minus_expenses' : 'net_entered',
      deductible_expenses_original: depreciation,
      net_amount_original: net,
      expense_detail_complete: true,
    }
  }
  if (rental_expense_path === 'itemized') {
    const expenseTotal = sumRentalExpenses(rental_expenses)
    const depreciation = Number(rental_depreciation_amount) || 0
    const net = Math.max(0, gross_amount_original - expenseTotal - depreciation)
    return {
      ...item,
      amount_basis: 'gross_minus_expenses',
      deductible_expenses_original: expenseTotal + depreciation,
      net_amount_original: net,
      expense_detail_complete: true,
    }
  }
  // Path not yet chosen — treat as incomplete
  return { ...item, expense_detail_complete: false }
}

// ── Rental section ────────────────────────────────────────────────────────────

function RentalSection({ item, rentalConfig, onChange }) {
  function update(patch) {
    const updated = { ...item, ...patch }
    onChange(deriveRentalNet(updated))
  }

  function updateExpense(key, value) {
    const updated = {
      ...item,
      rental_expenses: { ...item.rental_expenses, [key]: Number(value) || 0 },
    }
    onChange(deriveRentalNet(updated))
  }

  function updateDepreciationSource(source) {
    const updated = {
      ...item,
      rental_depreciation_source: source,
      // Reset derived fields when source changes
      rental_depreciation_amount: source === 'none' ? 0 : item.rental_depreciation_amount,
      rental_purchase_price: source === 'estimated_from_purchase_price' ? (item.rental_purchase_price || 0) : null,
    }
    onChange(deriveRentalNet(updated))
  }

  function updatePurchasePrice(price) {
    const depreciation = Math.round((Number(price) || 0) * 0.03)
    const updated = {
      ...item,
      rental_purchase_price: Number(price) || 0,
      rental_depreciation_amount: depreciation,
    }
    onChange(deriveRentalNet(updated))
  }

  // liveNet: before-depreciation figure — "Estimated net before depreciation" label.
  // liveNetFinal: after depreciation — "Net taxable rental income" label and engine net.
  // Neither applies FX — all original currency, conversion happens in the engine.
  const liveNet = useMemo(() => {
    if (item.rental_expense_path === 'fast_path_net') {
      const entered = item._fast_path_net_entered ?? item.net_amount_original
      return Number(entered) || 0
    }
    if (item.rental_expense_path === 'itemized') {
      const expenses = sumRentalExpenses(item.rental_expenses)
      return Math.max(0, item.gross_amount_original - expenses)
    }
    return null
  }, [item])

  const liveNetFinal = useMemo(() => {
    if (liveNet === null) return null
    const depreciation = Number(item.rental_depreciation_amount) || 0
    return Math.max(0, liveNet - depreciation)
  }, [liveNet, item.rental_depreciation_amount])

  const pathChosen = item.rental_expense_path !== null
  const showItemized = item.rental_expense_path === 'itemized'
  const showFastPath = item.rental_expense_path === 'fast_path_net'
  const depSource = item.rental_depreciation_source

  return (
    <div className="cal-rental-section">
      {/* Path toggle */}
      <div className="u-stack-xs">
        <div className="u-label-sm">Do you already know your net rental income before depreciation?</div>
        <div className="cal-rental-path-hint">Rent minus cash expenses like mortgage interest, insurance, repairs, fees, and property taxes. Depreciation is handled separately below.</div>
        <div className="cal-rental-path-options">
          <button
            className={`cal-rental-path-btn${item.rental_expense_path === 'fast_path_net' ? ' cal-rental-path-btn--active' : ''}`}
            onClick={() => update({ rental_expense_path: 'fast_path_net' })}
          >
            Yes — I'll enter net before depreciation
          </button>
          <button
            className={`cal-rental-path-btn${item.rental_expense_path === 'itemized' ? ' cal-rental-path-btn--active' : ''}`}
            onClick={() => update({ rental_expense_path: 'itemized' })}
          >
            No — help me itemize cash expenses
          </button>
        </div>
      </div>

      {/* Fast path: single net field */}
      {showFastPath && (
        <Field label="Net rental income before depreciation (after all cash expenses)">
          <AmountField
            value={item._fast_path_net_entered ?? item.net_amount_original}
            onChange={v => update({ _fast_path_net_entered: Number(v) || 0 })}
          />
        </Field>
      )}

      {/* Itemized path: gross + expense fields */}
      {showItemized && (
        <>
          <Field label="Gross rent received">
            <AmountField
              value={item.gross_amount_original}
              onChange={v => update({ gross_amount_original: Number(v) || 0 })}
            />
          </Field>

          <div className="cal-rental-expenses">
            <div className="u-label-caps">Cash expenses</div>
            {rentalConfig.expenseFields.map(({ key, label }) => (
              <Field
                key={key}
                label={label}
                helper={key === 'mortgage_interest' ? 'Interest only — not principal repayment.' : undefined}
              >
                <AmountField
                  value={item.rental_expenses?.[key] || 0}
                  onChange={v => updateExpense(key, v)}
                />
              </Field>
            ))}
          </div>

        </>
      )}

      {/* Depreciation — shown once any path is chosen */}
      {pathChosen && (
        <div className="cal-rental-depreciation">
          <div className="u-label-caps">Depreciation / amortización (Art. 23 LIRPF)</div>
          <div className="cal-rental-depreciation-hint">
            Spain may allow a depreciation deduction on the building portion of this property.
            Depreciation is separate from cash expenses.
          </div>
          <Field label="Depreciation">
            <Select
              value={depSource || ''}
              onChange={updateDepreciationSource}
              options={rentalConfig.depreciationOptions}
              placeholder="Choose an option…"
            />
          </Field>

          {depSource === 'user_entered' && (
            <Field label="Annual depreciation amount">
              <AmountField
                value={item.rental_depreciation_amount}
                onChange={v => update({ rental_depreciation_amount: Number(v) || 0 })}
              />
            </Field>
          )}

          {depSource === 'estimated_from_purchase_price' && (
            <>
              <Field label="Purchase price + acquisition costs">
                <AmountField
                  value={item.rental_purchase_price || 0}
                  onChange={updatePurchasePrice}
                />
              </Field>
              {(item.rental_purchase_price || 0) > 0 && (
                <div className="cal-rental-depreciation-estimate">
                  Estimated depreciation: <strong>{fmtRentalAmount(item.rental_depreciation_amount, item.currency)}/year</strong>
                  {' '}(3% of purchase price — Spain excludes land value; your gestor can confirm the exact figure)
                </div>
              )}
            </>
          )}

          {/* Net taxable summary — fast path only; itemized path uses breakdown table below */}
          {showFastPath && depSource && depSource !== 'none' && (
            <div className="cal-rental-net-final">
              Net taxable rental income:{' '}
              <strong>{fmtRentalAmount(liveNetFinal, item.currency)}</strong>
            </div>
          )}
        </div>
      )}

      {/* Breakdown table — itemized path, shown after depreciation section is complete */}
      {showItemized && pathChosen && (
        <div className="cal-rental-breakdown">
          <div className="cal-rental-breakdown-row">
            <span>Gross rent received</span>
            <span>{fmtRentalAmount(item.gross_amount_original, item.currency)}</span>
          </div>
          <div className="cal-rental-breakdown-row cal-rental-breakdown-row--expense">
            <span>Cash expenses</span>
            <span>−{fmtRentalAmount(sumRentalExpenses(item.rental_expenses), item.currency)}</span>
          </div>
          <div className="cal-rental-breakdown-row cal-rental-breakdown-row--subtotal">
            <span>Net before depreciation</span>
            <span>{fmtRentalAmount(liveNet, item.currency)}</span>
          </div>
          {depSource && depSource !== 'none' && (
            <>
              <div className="cal-rental-breakdown-row cal-rental-breakdown-row--expense">
                <span>Depreciation</span>
                <span>−{fmtRentalAmount(item.rental_depreciation_amount, item.currency)}</span>
              </div>
              <div className="cal-rental-breakdown-row cal-rental-breakdown-row--total">
                <span>Estimated taxable rental income</span>
                <span>{fmtRentalAmount(liveNetFinal, item.currency)}</span>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}

// ── Self-employment helpers ───────────────────────────────────────────────────

function sumBusinessExpenses(expenses) {
  if (!expenses) return 0
  return Object.values(expenses).reduce((sum, v) => sum + (Number(v) || 0), 0)
}

// Recomputes net_amount_original and amount_basis from self-employment fields.
// Mirrors deriveRentalNet() — no depreciation branch, business expenses have
// no equivalent concept in v1 (equipment goes in as a straight itemized
// expense, no amortization schedule).
//
// IMPORTANT ASYMMETRY: on the fast path, the user enters one net figure with
// no gross/expense breakdown. gross_amount_original is set equal to that net
// figure (not left at 0, unlike rental's fast path) because autonomoCuota.ts
// reads gross_amount_original and deductible_expenses_original directly to
// compute RETA's own 7%/3% reduction — it does not read net_amount_original.
// A fast-path entry therefore has RETA apply its reduction on top of a figure
// that's already net of the user's actual expenses, which understates the
// RETA contribution base somewhat. The itemized path doesn't have this
// problem — gross and expenses are both real numbers. Worth nudging users
// toward itemizing in copy, since the fast path is quietly less accurate for
// the Social Security side specifically (IRPF math is unaffected either way).
function deriveBusinessNet(item) {
  const { business_expense_path, gross_amount_original, business_expenses } = item
  if (business_expense_path === 'fast_path_net') {
    const entered = Number(item._fast_path_net_entered ?? item.net_amount_original) || 0
    return {
      ...item,
      amount_basis: 'net_entered',
      gross_amount_original: entered,
      deductible_expenses_original: 0,
      net_amount_original: entered,
      expense_detail_complete: true,
    }
  }
  if (business_expense_path === 'itemized') {
    const expenseTotal = sumBusinessExpenses(business_expenses)
    const net = Math.max(0, gross_amount_original - expenseTotal)
    return {
      ...item,
      amount_basis: 'gross_minus_expenses',
      deductible_expenses_original: expenseTotal,
      net_amount_original: net,
      expense_detail_complete: true,
    }
  }
  // Path not yet chosen — treat as incomplete
  return { ...item, expense_detail_complete: false }
}

// ── Self-employment section ───────────────────────────────────────────────────

function SelfEmploymentExpenseSection({ item, selfEmploymentConfig, onChange }) {
  function update(patch) {
    const updated = { ...item, ...patch }
    onChange(deriveBusinessNet(updated))
  }

  function updateExpense(key, value) {
    const updated = {
      ...item,
      business_expenses: { ...item.business_expenses, [key]: Number(value) || 0 },
    }
    onChange(deriveBusinessNet(updated))
  }

  const liveNet = useMemo(() => {
    if (item.business_expense_path === 'fast_path_net') {
      const entered = item._fast_path_net_entered ?? item.net_amount_original
      return Number(entered) || 0
    }
    if (item.business_expense_path === 'itemized') {
      const expenses = sumBusinessExpenses(item.business_expenses)
      return Math.max(0, item.gross_amount_original - expenses)
    }
    return null
  }, [item])

  const pathChosen = item.business_expense_path !== null
  const showItemized = item.business_expense_path === 'itemized'
  const showFastPath = item.business_expense_path === 'fast_path_net'
  const showWithholdingQuestion = item.activity_classification === 'profesional'
    || item.activity_classification === 'agricola_ganadera_forestal'

  return (
    <div className="cal-rental-section">
      {/* Path toggle */}
      <div className="u-stack-xs">
        <div className="u-label-sm">Do you know your total business expenses, or would you rather itemize?</div>
        <div className="cal-rental-path-hint">Itemizing gives a more accurate Social Security estimate alongside your income tax — worth doing if you can.</div>
        <div className="u-hint">Your RETA (Social Security) contributions are also deductible — we calculate and apply those automatically, so there's no need to enter them as a business expense here.</div>
        <div className="cal-rental-path-options">
          <button
            className={`cal-rental-path-btn${showFastPath ? ' cal-rental-path-btn--active' : ''}`}
            onClick={() => update({ business_expense_path: 'fast_path_net' })}
          >
            I know my net total
          </button>
          <button
            className={`cal-rental-path-btn${showItemized ? ' cal-rental-path-btn--active' : ''}`}
            onClick={() => update({ business_expense_path: 'itemized' })}
          >
            Help me itemize expenses
          </button>
        </div>
      </div>

      {/* Fast path: single net field */}
      {showFastPath && (
        <Field label="Net income after business expenses">
          <AmountField
            value={item._fast_path_net_entered ?? item.net_amount_original}
            onChange={v => update({ _fast_path_net_entered: Number(v) || 0 })}
          />
        </Field>
      )}

      {/* Itemized path: gross + expense fields */}
      {showItemized && (
        <>
          <Field label="Gross revenue / invoiced">
            <AmountField
              value={item.gross_amount_original}
              onChange={v => update({ gross_amount_original: Number(v) || 0 })}
            />
          </Field>

          <div className="cal-rental-expenses">
            <div className="u-label-caps">Business expenses</div>
            {selfEmploymentConfig.expenseFields.map(({ key, label }) => (
              <Field key={key} label={label}>
                <AmountField
                  value={item.business_expenses?.[key] || 0}
                  onChange={v => updateExpense(key, v)}
                />
              </Field>
            ))}
          </div>

          <div className="cal-rental-breakdown">
            <div className="cal-rental-breakdown-row">
              <span>Gross revenue</span>
              <span>{fmtRentalAmount(item.gross_amount_original, item.currency)}</span>
            </div>
            <div className="cal-rental-breakdown-row cal-rental-breakdown-row--expense">
              <span>Business expenses</span>
              <span>−{fmtRentalAmount(sumBusinessExpenses(item.business_expenses), item.currency)}</span>
            </div>
            <div className="cal-rental-breakdown-row cal-rental-breakdown-row--total">
              <span>Net business income</span>
              <span>{fmtRentalAmount(liveNet, item.currency)}</span>
            </div>
          </div>
        </>
      )}

      {/* Activity facts — feed autonomoCuota.ts, not the amount derivation above.
          Own class (cal-activity-facts), not shared with rental's
          cal-rental-depreciation — see CSS_GUIDELINES.md #4 on why reusing a
          class across two different sections is risky once either needs to
          diverge visually. */}
      {pathChosen && (
        <div className="cal-activity-facts">
          <div className="u-label-caps">About this activity</div>
          <Field
            label="What type of activity is this?"
            helper="Affects whether a Modelo 130 withholding exemption can apply."
          >
            <Select
              value={item.activity_classification || ''}
              onChange={v => update({ activity_classification: v || null })}
              options={ACTIVITY_CLASSIFICATION_OPTIONS}
              placeholder="Choose an option…"
            />
          </Field>
          <Field
            label="Did this activity start this tax year?"
            helper="Only affects which reference period we mention for the Modelo 130 withholding test — doesn't change your estimate."
          >
            <Select
              value={boolToOptionValue(item.activity_started_this_year)}
              onChange={v => update({ activity_started_this_year: optionValueToBool(v) })}
              options={YES_NO_OPTIONS}
              placeholder="Choose an option…"
            />
          </Field>
          {showWithholdingQuestion && (
            <Field
              label="What share of this income has Spanish withholding already applied?"
              helper="If you started this activity this year, use the period this quarter's payment covers. Otherwise use last year's figures. Enter a percentage, e.g. 80 for 80%."
            >
              <AmountField
                value={item.withholding_ratio === null ? '' : Math.round(item.withholding_ratio * 100)}
                onChange={v => update({ withholding_ratio: v === '' ? null : Math.max(0, Math.min(100, Number(v))) / 100 })}
              />
            </Field>
          )}
        </div>
      )}
    </div>
  )
}

// ── Single income item mini-form ─────────────────────────────────────────────

function IncomeItemForm({ item, cardConfig, onChange, onRemove, showRemove, renderItemExtra }) {
  // Tracks whether the Amount field has been blurred at least once — the
  // warning only appears after the user has actually left the field, not
  // while they're still typing. Derived (not stored) against amount_period
  // and gross_amount_original so it clears itself automatically if the user
  // fixes the period or the amount afterward — no separate reset needed.
  const [amountTouched, setAmountTouched] = useState(false)
  const showPeriodWarning = amountTouched
    && item.amount_period === 'annual'
    && item.gross_amount_original > 0
    && item.gross_amount_original < LOW_ANNUAL_INCOME_THRESHOLD

  function update(patch) {
    onChange({ ...item, ...patch })
  }

  const isRental = cardConfig.income_type === 'rental'
  const isSelfEmployment = cardConfig.income_type === 'self_employment'
  // Rental and self-employment own their amount_basis via their own section —
  // suppress the standard gross/net select for both.
  const showAmountBasis = !isRental && !isSelfEmployment && AMOUNT_BASIS_ASK_TYPES.has(cardConfig.income_type)
  const showSubtype = !!cardConfig.subtypeOptions
  const showSourceCountry = cardConfig.askSourceCountry

  // Show "foreign tax paid" when we know this income has a non-Spanish source.
  // retirement_distribution has no source country selector but always defaults
  // to 'US' — condition is still true so the field appears there too.
  // Rental with a Spanish property subtype will have source_country = 'ES'
  // (set by the user) so the field stays hidden for domestic rentals.
  const isForeignSource = item.source_country && item.source_country !== 'ES'
  const showForeignTaxPaid = !!isForeignSource

  return (
    <div className="cal-income-item">

      {showRemove && (
        <button className="cal-income-item-remove" onClick={onRemove} aria-label="Remove">
          ✕
        </button>
      )}

      {/* Label */}
      <Field label="What is this income?">
        <input
          type="text"
          className="profile-input"
          value={item.label}
          onChange={e => update({ label: e.target.value })}
          placeholder={cardConfig.label}
        />
      </Field>

      {/* Subtype */}
      {showSubtype && (
        <Field label="More specifically">
          <Select
            value={item.income_subtype}
            onChange={v => update({ income_subtype: v || null })}
            options={cardConfig.subtypeOptions}
          />
        </Field>
      )}

      {/* Source country */}
      {showSourceCountry && (
        <Field
          label={cardConfig.sourceCountryLabel || 'Where is this income from?'}
          helper={cardConfig.sourceCountryHint}
        >
          <Select
            value={item.source_country}
            onChange={v => update({
              source_country: v || null,
              source_country_status: v ? 'provided' : 'unknown',
            })}
            options={SOURCE_COUNTRY_OPTIONS}
            placeholder="Select a country…"
          />
        </Field>
      )}

      {/* Standard amount fields — not shown for rental or self-employment */}
      {!isRental && !isSelfEmployment && (
        <>
          <div className="cal-amount-row">
            <Field
              label="Amount"
              helper={cardConfig.amountHint}
              warning={showPeriodWarning ? PERIOD_MISMATCH_WARNING : null}
            >
              <AmountField
                value={item.gross_amount_original}
                onChange={v => update({
                  gross_amount_original: v,
                  net_amount_original: v,
                })}
                onBlur={() => setAmountTouched(true)}
              />
            </Field>
            <Field label="Period">
              <Select
                value={item.amount_period}
                onChange={v => update({ amount_period: v })}
                options={PERIOD_OPTIONS}
              />
            </Field>
            <Field label="Currency">
              <Select
                value={item.currency}
                onChange={v => update({ currency: v })}
                options={CURRENCY_OPTIONS}
              />
            </Field>
          </div>

          {showAmountBasis && (
            <Field label="Is this gross or net?">
              <Select
                value={item.amount_basis}
                onChange={v => update({ amount_basis: v })}
                options={AMOUNT_BASIS_OPTIONS}
              />
            </Field>
          )}
        </>
      )}

      {/* Rental-specific section — period + currency above, expenses below */}
      {isRental && (
        <>
          <div className="cal-amount-row cal-amount-row--rental-meta">
            <Field label="Period">
              <Select
                value={item.amount_period}
                onChange={v => update({ amount_period: v })}
                options={PERIOD_OPTIONS}
              />
            </Field>
            <Field label="Currency">
              <Select
                value={item.currency}
                onChange={v => update({ currency: v })}
                options={CURRENCY_OPTIONS}
              />
            </Field>
          </div>
          <RentalSection
            item={item}
            rentalConfig={cardConfig.rentalConfig}
            onChange={onChange}
          />
        </>
      )}

      {/* Self-employment-specific section — period + currency above, expenses below */}
      {isSelfEmployment && (
        <>
          <div className="cal-amount-row cal-amount-row--rental-meta">
            <Field label="Period">
              <Select
                value={item.amount_period}
                onChange={v => update({ amount_period: v })}
                options={PERIOD_OPTIONS}
              />
            </Field>
            <Field label="Currency">
              <Select
                value={item.currency}
                onChange={v => update({ currency: v })}
                options={CURRENCY_OPTIONS}
              />
            </Field>
          </div>
          <SelfEmploymentExpenseSection
            item={item}
            selfEmploymentConfig={cardConfig.selfEmploymentConfig}
            onChange={onChange}
          />
        </>
      )}

      {/* Foreign tax paid — shown for any non-Spanish income source.
          Does not reduce the estimate; triggers the foreign_tax_relief review
          flag so the result page can note the potential credit for advisor review.
          Intentionally placed last so it doesn't distract from the primary fields. */}
      {showForeignTaxPaid && (
        <Field
          label="Foreign tax paid on this income"
          helper="Optional. If you paid tax to a foreign government on this income (e.g. US withholding or UK PAYE), enter the amount. Spain may allow an offset — this is flagged in your results for professional review but does not reduce this estimate."
        >
          <AmountField
            value={item.tax_paid_foreign}
            onChange={v => update({ tax_paid_foreign: v === '' ? 0 : Number(v) })}
            placeholder="0"
          />
        </Field>
      )}

      {/* Optional per-item slot (ticket 064: the intake's work-location question
          for employment). Renders nothing when the prop is not passed, so the
          Calculator is unchanged. */}
      {renderItemExtra ? renderItemExtra(item) : null}

    </div>
  )
}

// Returns true if the user has entered anything meaningful — used to decide
// whether collapsing should save (meaningful input exists) or cancel (nothing entered).
function hasAnyMeaningfulInput(items, cardConfig) {
  return items.some(item => {
    if (cardConfig.income_type === 'rental') {
      // Rental is meaningful once the user has chosen an expense path
      return item.rental_expense_path !== null
    }
    if (cardConfig.income_type === 'self_employment') {
      // Same reasoning as rental — meaningful once a path is chosen
      return item.business_expense_path !== null
    }
    return (item.gross_amount_original || 0) > 0
  })
}

// ── Card ──────────────────────────────────────────────────────────────────────

export default function IncomeCard({ cardConfig, items, onItemsChange, adjustmentsForm, setAdjustment, startExpanded = false, owner = 'self', renderItemExtra }) {
  const enabled = items.length > 0
  const [expanded, setExpanded] = useState(startExpanded)

  function add() {
    // Fires once per card type turned on — the "user has this income type"
    // signal. income_subtype isn't known yet at this point (chosen inside
    // the expanded card afterward), so it's intentionally left out rather
    // than captured as null.
    posthog.capture('income_card_added', { card_type: cardConfig.income_type, owner })
    onItemsChange([createEmptyIncomeItem(cardConfig, owner)])
    setExpanded(true)
  }

  function deleteAll() {
    onItemsChange([])
    setExpanded(false)
  }

  function addAnother() {
    onItemsChange([...items, createEmptyIncomeItem(cardConfig, owner)])
  }

  function updateItem(index, updated) {
    const next = [...items]
    next[index] = updated
    onItemsChange(next)
  }

  function removeItem(index) {
    const next = items.filter((_, i) => i !== index)
    // If last item removed, treat as delete
    if (next.length === 0) {
      onItemsChange([])
      setExpanded(false)
    } else {
      onItemsChange(next)
    }
  }

  const itemSummary = items.length === 1 ? '1 item' : `${items.length} items`

  // ── Disabled tile ──────────────────────────────────────────────────────
  if (!enabled) {
    return (
      <div className="cal-income-card">
        <button className="cal-income-card-header" onClick={add} aria-label={`Add ${cardConfig.label}`}>
          <div className="cal-income-card-icon-chip">
            <i className={`ti ${cardConfig.tablerIcon}`} aria-hidden="true" />
          </div>
          <div className="cal-income-card-text">
            <div className="u-label-sm">{cardConfig.label}</div>
            <div className="u-desc-xs">{cardConfig.description}</div>
            {cardConfig.excludedFromEstimate && (
              <div className="cal-income-card-excluded-badge">{cardConfig.excludedNote}</div>
            )}
            {cardConfig.usOnly && (
              <span className="cal-income-card-us-badge">🇺🇸 only</span>
            )}
          </div>
          <span className="cal-income-card-add">+ Add</span>
        </button>
      </div>
    )
  }

  // ── Enabled + collapsed ────────────────────────────────────────────────
  if (!expanded) {
    return (
      <div className="cal-income-card cal-income-card--active">
        <div className="cal-income-card-header cal-income-card-header--active">
          <div className="cal-income-card-icon-chip cal-income-card-icon-chip--on">
            <i className={`ti ${cardConfig.tablerIcon}`} aria-hidden="true" />
          </div>
          <div className="cal-income-card-text">
            <div className="u-label-sm">{cardConfig.label}</div>
            <span className="cal-income-card-summary">{itemSummary}</span>
          </div>
          <div className="cal-income-card-actions">
            <button
              className="cal-income-card-action-btn"
              onClick={() => setExpanded(true)}
              aria-label="Expand"
              title="Edit"
            >
              <i className="ti ti-chevron-down" aria-hidden="true" />
            </button>
            <button
              className="cal-income-card-action-btn cal-income-card-action-btn--delete"
              onClick={deleteAll}
              aria-label="Remove"
              title="Remove"
            >
              <i className="ti ti-x" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    )
  }

  // ── Enabled + expanded ─────────────────────────────────────────────────
  return (
    <div className="cal-income-card cal-income-card--open">
      <div className="cal-income-card-header cal-income-card-header--active">
        <div className="cal-income-card-icon-chip cal-income-card-icon-chip--on">
          <i className={`ti ${cardConfig.tablerIcon}`} aria-hidden="true" />
        </div>
        <div className="cal-income-card-text">
          <div className="u-label-sm">{cardConfig.label}</div>
        </div>
        <div className="cal-income-card-actions">
          <button
            className="cal-income-card-action-btn"
            onClick={() => {
              if (hasAnyMeaningfulInput(items, cardConfig)) {
                setExpanded(false)
              } else {
                deleteAll()
              }
            }}
            aria-label="Collapse"
            title="Collapse"
          >
            <i className="ti ti-chevron-up" aria-hidden="true" />
          </button>
          <button
            className="cal-income-card-action-btn cal-income-card-action-btn--delete"
            onClick={deleteAll}
            aria-label="Remove"
            title="Remove"
          >
            <i className="ti ti-x" aria-hidden="true" />
          </button>
        </div>
      </div>

      <div className="cal-income-card-body">
        {items.map((item, i) => (
          <IncomeItemForm
            key={item.id}
            item={item}
            cardConfig={cardConfig}
            onChange={updated => updateItem(i, updated)}
            onRemove={() => removeItem(i)}
            showRemove={items.length > 1}
            renderItemExtra={renderItemExtra}
          />
        ))}
        <button className="cal-add-another" onClick={addAnother}>
          + Add another {cardConfig.label.toLowerCase()}
        </button>
      </div>
    </div>
  )
}